import React, { forwardRef, useEffect, useState } from 'react';
import OazaLogo from "../../image/logo/logo_color_300x70.png";

const InvoiceTemplate = forwardRef(({ transactionData }, ref) => {
  const [processedHtml, setProcessedHtml] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Function to get image as base64 using CORS proxy (working solution)
  const getImageAsBase64 = async (url) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      throw error;
    }
  };
  
  useEffect(() => {
    if (typeof transactionData === 'string' && transactionData.includes('<')) {
      setIsProcessing(true);
      processImages();
    } else {
      setProcessedHtml('');
      setIsProcessing(false);
    }
  }, [transactionData]);

  // Process HTML to handle external images with multiple approaches
  const processImages = async () => {
    let html = transactionData;
    
    try {
      // Find all img tags with src attributes
      const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
      const matches = [...html.matchAll(imgRegex)];
      
      
      for (const match of matches) {
        const fullImgTag = match[0];
        const imageUrl = match[1];
        
        if (imageUrl.startsWith('http') || imageUrl.startsWith('//')) {
          let base64Image = null;
          
          try {            
            // Try CORS proxy services (working solution)
            const proxyServices = [
              `https://images.weserv.nl/?url=${encodeURIComponent(imageUrl)}&w=300&h=100&fit=contain`,
              `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(imageUrl)}`,
            ];
            
            for (const proxyUrl of proxyServices) {
              try {
                base64Image = await getImageAsBase64(proxyUrl);
                if (base64Image) {
                  const newImgTag = fullImgTag.replace(
                    /src=["']([^"']+)["']/i,
                    `src="${base64Image}"`
                  );
                  html = html.replace(fullImgTag, newImgTag);
                  break;
                }
              } catch (proxyError) {
                continue; // Try next proxy
              }
            }
            
            // If all proxies failed, use local logo fallback
            if (!base64Image) {
              throw new Error('All proxy services failed');
            }
          } catch (error) {
            // Fallback to local logo instead of placeholder
            try {
              // Convert local logo to base64 for PDF compatibility
              const localLogoBase64 = await getImageAsBase64(OazaLogo);
              
              const newImgTag = fullImgTag.replace(
                /src=["']([^"']+)["']/i,
                `src="${localLogoBase64}"`
              );
              html = html.replace(fullImgTag, newImgTag);
            } catch (localError) {
              console.log('Local logo conversion failed:', localError);
              // Final fallback - use direct path (might work in some cases)
              const newImgTag = fullImgTag.replace(
                /src=["']([^"']+)["']/i,
                `src="${OazaLogo}"`
              );
              html = html.replace(fullImgTag, newImgTag);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error processing images:', error);
    }
    
    setProcessedHtml(html);
    setIsProcessing(false);
  };
  
  // Show loading state while processing images
  if (isProcessing) {
    return (
      <div ref={ref} style={{ padding: '20px', textAlign: 'center' }}>
        <p>Processing invoice template and loading images...</p>
      </div>
    );
  }
  
  // If transactionData is HTML string, render the processed version
  if (processedHtml) {
    return (
      <div 
        ref={ref}
        dangerouslySetInnerHTML={{ __html: processedHtml }}
        style={{ 
          fontFamily: 'Arial, sans-serif',
          maxWidth: '800px',
          margin: '0 auto',
          backgroundColor: '#ffffff',
          padding: '0',  // Remove padding
          boxSizing: 'border-box'
        }}
      />
    );
  }
  
  // If no HTML content, render a simple message
  return (
    <div ref={ref} style={{ padding: '20px', textAlign: 'center' }}>
      <p>No invoice data available</p>
    </div>
  );
});

InvoiceTemplate.displayName = 'InvoiceTemplate';

export default InvoiceTemplate;
